import * as signalR from "@microsoft/signalr";

/**
 * Single shared SignalR connection to the backend's chat hub (`/chathub`).
 * Both live notifications (see NotificationContext) and in-app chat (see
 * ChatContext) ride this one connection — the backend pushes
 * "ReceiveLiveNotification" through the same hub chat runs on
 * (NotificationService holds an IHubContext<ChatHub>, not a dedicated
 * notifications hub), and mobile's own chat screen connects to the identical
 * hub. `acquireConnection`/`releaseConnection` are ref-counted so either
 * context mounting/unmounting independently can never tear down the other's
 * connection out from under it.
 *
 * Auth: the backend's JwtBearerEvents.OnMessageReceived reads the JWT from
 * the `access_token` query string specifically for hub paths, so
 * accessTokenFactory below is required, not optional.
 */

const HUB_PATH = "/chathub";
const LIVE_NOTIFICATION_EVENT = "ReceiveLiveNotification";

const getHubUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const origin = apiUrl.replace(/\/api\/?$/, "");
  return `${origin}${HUB_PATH}`;
};

let connection = null;
let consumers = 0;

const notificationListeners = new Set();
// Fires after withAutomaticReconnect() re-establishes the connection.
// SignalR group membership (JoinGroup) lives on the physical connection ID
// server-side, not this HubConnection object - a reconnect (idle timeout
// from a reverse proxy/load balancer, a network blip, laptop sleep/wake)
// gets a brand-new connection ID transparently, silently dropping whatever
// groups were joined before. invoke() calls keep working the whole time
// (ensureConnected sees Connected and proceeds), so nothing looks broken -
// the room the user has open just stops receiving ReceiveMessage broadcasts
// until something re-runs JoinGroup. ChatContext listens here to do that.
const reconnectedListeners = new Set();

// --- Chat event listener sets, ported from mobile's signal-r-service ---
const chatHistoryListeners = new Set();
const chatMessageListeners = new Set();
const chatMessagesBatchListeners = new Set();
const chatMessageDeletedListeners = new Set();
const chatMessageEditedListeners = new Set();
const chatMessageDeliveredListeners = new Set();
const chatMessageSeenListeners = new Set();
const chatRoomUsersListeners = new Set();
const chatUserOnlineListeners = new Set();
const chatUserOfflineListeners = new Set();
const chatUserBlockStatusListeners = new Set();
const chatUserKickedListeners = new Set();
const chatUserLeftListeners = new Set();
// Chat media approval gate (ChatRoom.RequireMediaApproval) — see
// chatMediaApprovalService.js. "Submitted" is a bare ping (no payload beyond
// the room id) telling a moderator something's waiting; a live sender who
// gets held or later rejected hears about their own message directly.
const pendingChatMessageListeners = new Set();
const chatMessageRejectedListeners = new Set();

const notify = (listeners, ...args) => {
  listeners.forEach((cb) => {
    try {
      cb(...args);
    } catch {
      // one bad listener shouldn't break the others
    }
  });
};

// Diagnostic switch used to catch several real hub-payload-shape mismatches
// during Phase 1 development (ReceiveMessage's arg count, MessageDelivered/
// MessageSeen's single-object vs array shape, MessageEdited's field name —
// see promoter-panel-chat.md). Left in place, off by default — flip to
// true and filter the console for "chat-debug" if a new shape surprise
// shows up (media messages in Phase 2 are the next likely source of one).
const CHAT_DEBUG = true;
const debugLog = (eventName, args) => {
  if (!CHAT_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(`[chat-debug] ${eventName} raw args (${args.length}):`, ...args);
};

/** (Re)registers every `.on` handler — called on connect and after every reconnect, since a fresh connection object has no handlers of its own. */
const registerHandlers = () => {
  connection.off(LIVE_NOTIFICATION_EVENT);
  connection.on(LIVE_NOTIFICATION_EVENT, (payload) => notify(notificationListeners, payload));

  connection.off("ReceiveChatHistory");
  connection.on("ReceiveChatHistory", (messages) => {
    debugLog("ReceiveChatHistory", [messages]);
    notify(chatHistoryListeners, messages);
  });

  // Confirmed live: single argument, the full message object
  // {id, messageId, eventId, sender, message, ...} — not (userId, message)
  // the way mobile's client-side wrapper implied.
  connection.off("ReceiveMessage");
  connection.on("ReceiveMessage", (message) => {
    debugLog("ReceiveMessage", [message]);
    notify(chatMessageListeners, message);
  });

  connection.off("ReceiveMessagesBatch");
  connection.on("ReceiveMessagesBatch", (messages) => {
    debugLog("ReceiveMessagesBatch", [messages]);
    notify(chatMessagesBatchListeners, messages);
  });

  connection.off("MessageDeleted");
  connection.on("MessageDeleted", (...args) => {
    debugLog("MessageDeleted", args);
    notify(chatMessageDeletedListeners, args[0], args[1]);
  });

  connection.off("MessageEdited");
  connection.on("MessageEdited", (...args) => {
    debugLog("MessageEdited", args);
    notify(chatMessageEditedListeners, args[0]);
  });

  connection.off("MessageDelivered");
  connection.on("MessageDelivered", (...args) => {
    debugLog("MessageDelivered", args);
    notify(chatMessageDeliveredListeners, args[0]);
  });

  connection.off("MessageSeen");
  connection.on("MessageSeen", (...args) => {
    debugLog("MessageSeen", args);
    notify(chatMessageSeenListeners, args[0]);
  });

  connection.off("ReceiveChatRoomUsers");
  connection.on("ReceiveChatRoomUsers", (...args) => {
    debugLog("ReceiveChatRoomUsers", args);
    notify(chatRoomUsersListeners, args[0], args[1]);
  });

  connection.off("UserOnline");
  connection.on("UserOnline", (...args) => {
    debugLog("UserOnline", args);
    notify(chatUserOnlineListeners, args[0]);
  });

  connection.off("UserOffline");
  connection.on("UserOffline", (...args) => {
    debugLog("UserOffline", args);
    notify(chatUserOfflineListeners, args[0]);
  });

  connection.off("ChatUserBlockStatusChanged");
  connection.on("ChatUserBlockStatusChanged", (...args) => {
    debugLog("ChatUserBlockStatusChanged", args);
    notify(chatUserBlockStatusListeners, args[0]);
  });

  connection.off("UserKicked");
  connection.on("UserKicked", (...args) => {
    debugLog("UserKicked", args);
    notify(chatUserKickedListeners, args[0]);
  });

  connection.off("UserLeftRoom");
  connection.on("UserLeftRoom", (...args) => {
    debugLog("UserLeftRoom", args);
    notify(chatUserLeftListeners, args[0]);
  });

  connection.off("PendingChatMessageSubmitted");
  connection.on("PendingChatMessageSubmitted", (payload) => {
    debugLog("PendingChatMessageSubmitted", [payload]);
    notify(pendingChatMessageListeners, payload);
  });

  connection.off("ChatMessageRejected");
  connection.on("ChatMessageRejected", (payload) => {
    debugLog("ChatMessageRejected", [payload]);
    notify(chatMessageRejectedListeners, payload);
  });
};

/** Idempotent, ref-counted — safe to call from multiple contexts independently mounting/unmounting. */
const acquireConnection = (getToken) => {
  consumers += 1;
  if (connection) return connection;

  connection = new signalR.HubConnectionBuilder()
    .withUrl(getHubUrl(), { accessTokenFactory: getToken })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  registerHandlers();
  connection.onreconnected(() => {
    registerHandlers();
    notify(reconnectedListeners);
  });

  connection.start().catch(() => {
    // Live updates are a nice-to-have on top of REST — a failed/blocked
    // WebSocket connection must never break the panel.
  });

  return connection;
};

/** Only actually stops the connection once every consumer has released it. */
const releaseConnection = () => {
  consumers = Math.max(0, consumers - 1);
  if (consumers > 0 || !connection) return;
  connection.stop().catch(() => {});
  connection = null;
};

/**
 * Waits for the shared connection to be usable before an invoke proceeds.
 * Deliberately tolerates `connection` being `null` at call time (not just
 * "not yet Connected") and polls until one appears — a caller (e.g.
 * ChatThreadPage's mount effect) can legitimately run before whichever
 * provider's own mount effect calls acquireConnection() first, since
 * React doesn't guarantee sibling/parent effect ordering across the tree.
 * Confirmed live: without this, JoinGroup/SendMessage were being silently
 * skipped on first render of a chat thread.
 */
const ensureConnected = () => new Promise((resolve) => {
  const startedAt = Date.now();
  const timedOut = () => Date.now() - startedAt > 8000;

  const check = () => {
    if (!connection) {
      if (timedOut()) return resolve(false);
      setTimeout(check, 100);
      return;
    }
    if (connection.state === signalR.HubConnectionState.Connected) return resolve(true);
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      connection.start()
        .then(() => resolve(connection?.state === signalR.HubConnectionState.Connected))
        .catch(() => resolve(false));
      return;
    }
    // Connecting/Reconnecting — poll rather than invoking mid-handshake.
    if (timedOut()) return resolve(false);
    setTimeout(check, 150);
  };

  check();
});

const invoke = (...args) =>
  ensureConnected().then((ok) => {
    if (!ok) {
      if (CHAT_DEBUG) console.log(`[chat-debug] invoke("${args[0]}") skipped — hub not connected`); // eslint-disable-line no-console
      return undefined;
    }
    return connection.invoke(...args).catch((err) => {
      if (CHAT_DEBUG) console.log(`[chat-debug] invoke("${args[0]}") threw:`, err); // eslint-disable-line no-console
      return undefined;
    });
  });

// --- Public API: notifications (unchanged surface — existing callers keep working) ---
export const connectNotificationHub = (getToken) => acquireConnection(getToken);
export const disconnectNotificationHub = () => releaseConnection();
export const subscribeLiveNotification = (callback) => {
  notificationListeners.add(callback);
  return () => notificationListeners.delete(callback);
};
/** Fires after every automatic reconnect — see reconnectedListeners above. */
export const subscribeReconnected = (callback) => {
  reconnectedListeners.add(callback);
  return () => reconnectedListeners.delete(callback);
};

// --- Public API: chat ---
export const connectChatHub = (getToken) => acquireConnection(getToken);
export const disconnectChatHub = () => releaseConnection();

export const subscribeChatHistory = (cb) => { chatHistoryListeners.add(cb); return () => chatHistoryListeners.delete(cb); };
export const subscribeChatMessage = (cb) => { chatMessageListeners.add(cb); return () => chatMessageListeners.delete(cb); };
export const subscribeChatMessagesBatch = (cb) => { chatMessagesBatchListeners.add(cb); return () => chatMessagesBatchListeners.delete(cb); };
export const subscribeChatMessageDeleted = (cb) => { chatMessageDeletedListeners.add(cb); return () => chatMessageDeletedListeners.delete(cb); };
export const subscribeChatMessageEdited = (cb) => { chatMessageEditedListeners.add(cb); return () => chatMessageEditedListeners.delete(cb); };
export const subscribeChatMessageDelivered = (cb) => { chatMessageDeliveredListeners.add(cb); return () => chatMessageDeliveredListeners.delete(cb); };
export const subscribeChatMessageSeen = (cb) => { chatMessageSeenListeners.add(cb); return () => chatMessageSeenListeners.delete(cb); };
export const subscribeChatRoomUsers = (cb) => { chatRoomUsersListeners.add(cb); return () => chatRoomUsersListeners.delete(cb); };
export const subscribeChatUserOnline = (cb) => { chatUserOnlineListeners.add(cb); return () => chatUserOnlineListeners.delete(cb); };
export const subscribeChatUserOffline = (cb) => { chatUserOfflineListeners.add(cb); return () => chatUserOfflineListeners.delete(cb); };
export const subscribeChatUserBlockStatus = (cb) => { chatUserBlockStatusListeners.add(cb); return () => chatUserBlockStatusListeners.delete(cb); };
export const subscribeChatUserKicked = (cb) => { chatUserKickedListeners.add(cb); return () => chatUserKickedListeners.delete(cb); };
export const subscribeChatUserLeft = (cb) => { chatUserLeftListeners.add(cb); return () => chatUserLeftListeners.delete(cb); };
export const subscribePendingChatMessage = (cb) => { pendingChatMessageListeners.add(cb); return () => pendingChatMessageListeners.delete(cb); };
export const subscribeChatMessageRejected = (cb) => { chatMessageRejectedListeners.add(cb); return () => chatMessageRejectedListeners.delete(cb); };

// Invoke wrappers — argument order/types match mobile's signal-r-service exactly.
export const joinChatGroup = (eventId) => invoke("JoinGroup", Number(eventId));
export const leaveChatGroup = (eventId, userId) => invoke("LeaveGroup", Number(eventId), userId);
export const getChatHistory = (eventId) => invoke("GetChatHistory", `${eventId}`);
export const sendChatMessage = (eventId, userId, message, messageType = "text") =>
  invoke("SendMessage", Number(eventId), userId, message, messageType);
export const editChatMessage = (localId, messageId, message) =>
  invoke("EditMessage", message, Number.isNaN(Number(localId)) ? null : localId, messageId);
export const deleteChatMessage = (localId, messageId) =>
  invoke("DeleteMessage", Number.isNaN(Number(localId)) ? null : localId, messageId);
export const acknowledgeChatDelivery = (messageId) => invoke("AcknowledgeDelivery", messageId);
export const markChatMessageSeen = (messageId) => invoke("MarkMessageSeen", messageId);
export const getChatRoomUsers = (eventId) => invoke("GetChatRoomUsersWithStatus", Number(eventId));
export const leaveChatRoom = (eventId, userId) => invoke("LeaveChatRoom", eventId, userId);

// --- Dev-only manual test helper for the media approval gate ---
// This panel's own composer is text-only (media send is mobile-only), so
// there's no button anywhere here to send an "image"/"video" message. Rather
// than a separate script (SignalR-over-Node has its own compatibility
// wrinkles), this reuses the exact connection/auth already live on whichever
// account is logged into this tab. Stripped out of production builds
// (NODE_ENV === "production") — never reaches a real user.
if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
  window.sendTestChatMedia = (eventId, url, messageType = "image") => {
    let userId;
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      userId = stored?.Id ?? stored?.UserId ?? stored?.BusinessDetails?.UserId;
    } catch {
      userId = undefined;
    }
    if (!userId) {
      // eslint-disable-next-line no-console
      console.warn("[chat-debug] sendTestChatMedia: couldn't resolve a logged-in userId from localStorage.");
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.log(`[chat-debug] sendTestChatMedia -> eventId=${eventId} userId=${userId} messageType=${messageType} url=${url}`);
    return sendChatMessage(eventId, userId, url, messageType);
  };
}
